import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useSupabase } from '../contexts/SupabaseContext';
import { useErrorHandler } from './useErrorHandler';
import { usePerformance } from './usePerformance';
import { APIOptimizer } from '../lib/performance';
import { InputValidator } from '../lib/security';
import { products as mockProducts } from '../data/products';

export interface Product {
  id: string;
  name: string;
  slug: string;
  sku: string | null;
  price: number;
  original_price: number | null;
  image_url: string | null;
  rating: number;
  reviews: number;
  category_id: string;
  subcategory_id: string | null;
  supplier_id: string;
  description: string | null;
  features: string[];
  in_stock: boolean;
  stock_quantity: number;
  status: string;
  badge: string | null;
  created_at: string;
  quantity_prices: Array<{min_quantity: number, price: number}>;
  updated_at: string;
  // Joined fields
  category_name?: string;
  subcategory_name?: string;
  supplier_name?: string;
  supplier_location?: string;
}

export const useProducts = () => {
  const { user } = useSupabase();
  const { handleSupabaseError } = useErrorHandler();
  const { measureAsync } = usePerformance('useProducts');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Try to fetch from database first
        try {
          // Use cached fetch for better performance
          const response = await APIOptimizer.cachedFetch(
            `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/products?select=*,categories:category_id(name),subcategories:subcategory_id(name),suppliers:supplier_id(organization,location)&order=created_at.desc`,
            {
              headers: {
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
              }
            },
            300000 // 5 minute cache
          );
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const data = await response.json();
          
          if (data && data.length > 0) {
            // Transform the data to match our interface
            const transformedProducts = data.map((product: any) => ({
              ...product,
              quantity_prices: product.quantity_prices || [],
              category_name: product.categories?.name,
              subcategory_name: product.subcategories?.name,
              supplier_name: product.suppliers?.organization,
              supplier_location: product.suppliers?.location
            }));
            
            setProducts(transformedProducts);
          } else {
            // Use mock data if no database products
            setProducts(mockProducts);
          }
        } catch (dbError) {
          handleSupabaseError(dbError, 'fetchProducts', false);
          setProducts(mockProducts);
        }
      } catch (err) {
        handleSupabaseError(err, 'fetchProducts', false);
        // Fallback to mock data on any error
        setProducts(mockProducts);
      } finally {
        setLoading(false);
      }
    };

    const measureAndFetch = async () => {
      await measureAsync(fetchProducts, 'fetchProducts');
    };

    measureAndFetch();
  }, [measureAsync, handleSupabaseError]);

  const getProductById = (id: string): Product | undefined => {
    return products.find(product => product.id === id);
  };

  const getProductsByCategory = (categorySlug: string): Product[] => {
    return products.filter(product => 
      product.category_name?.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '') === categorySlug
    );
  };

  const getProductsBySubcategory = (subcategorySlug: string): Product[] => {
    return products.filter(product => 
      product.subcategory_name?.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '') === subcategorySlug
    );
  };

  const getProductsBySeller = (sellerSlug: string): Product[] => {
    return products.filter(product => 
      product.supplier_name?.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '') === sellerSlug
    );
  };

  const getFeaturedProducts = (limit: number = 8): Product[] => {
    return products
      .filter(product => product.badge)
      .slice(0, limit);
  };

  const getRelatedProducts = (productId: string, limit: number = 4): Product[] => {
    const product = getProductById(productId);
    if (!product) return [];
    
    return products
      .filter(p => p.id !== productId && p.category_id === product.category_id)
      .slice(0, limit);
  };

  return { 
    products, 
    loading, 
    error,
    getProductById,
    getProductsByCategory,
    getProductsBySubcategory,
    getProductsBySeller,
    getFeaturedProducts,
    getRelatedProducts
  };
};

// Function to create a new product
export const createProduct = async (productData: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    // Validate input data
    if (!productData.name || productData.name.trim().length === 0) {
      throw new Error('Product name is required');
    }
    
    if (!productData.price || productData.price <= 0) {
      throw new Error('Valid product price is required');
    }
    
    if (!productData.supplier_id) {
      throw new Error('Supplier ID is required');
    }
    
    // Sanitize text inputs
    const sanitizedData = {
      ...productData,
      name: InputValidator.sanitizeHtml(productData.name.trim()),
      description: productData.description ? InputValidator.sanitizeHtml(productData.description.trim()) : null,
      features: productData.features.map(feature => InputValidator.sanitizeHtml(feature.trim())).filter(Boolean)
    };
    
    // Insert the product into the database
    const { data, error } = await supabase
      .from('products')
      .insert(sanitizedData)
      .select()
      .single();
    
    if (error) {
      logger.error('Product creation failed', 'Products', { error, productData: sanitizedData });
      throw error;
    }
    
    logger.info('Product created successfully', 'Products', { productId: data.id, name: sanitizedData.name });
    
    return { success: true, data };
  } catch (err) {
    logger.error('Product creation error', 'Products', { error: err, productData });
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'An unknown error occurred' 
    };
  }
};

// Function to update an existing product
const updateProduct = async (productId: string, productData: Partial<Product>): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    // Validate product ID
    if (!productId || productId.trim().length === 0) {
      throw new Error('Product ID is required');
    }
    
    // Sanitize text inputs if provided
    const sanitizedData: Partial<Product> = { ...productData };
    
    if (sanitizedData.name) {
      sanitizedData.name = InputValidator.sanitizeHtml(sanitizedData.name.trim());
    }
    
    if (sanitizedData.description) {
      sanitizedData.description = InputValidator.sanitizeHtml(sanitizedData.description.trim());
    }
    
    if (sanitizedData.features) {
      sanitizedData.features = sanitizedData.features
        .map(feature => InputValidator.sanitizeHtml(feature.trim()))
        .filter(Boolean);
    }
    
    // Update the product in the database
    const { data, error } = await supabase
      .from('products')
      .update(sanitizedData)
      .eq('id', productId)
      .select()
      .single();
    
    if (error) {
      logger.error('Product update failed', 'Products', { error, productId, productData: sanitizedData });
      throw error;
    }
    
    logger.info('Product updated successfully', 'Products', { productId, name: data.name });
    
    // Clear cache for this product
    APIOptimizer.clearCache('products');
    
    return { success: true, data };
  } catch (err) {
    logger.error('Product update error', 'Products', { error: err, productId, productData });
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'An unknown error occurred' 
    };
  }
};

// Function to delete a product
const deleteProduct = async (productId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Validate product ID
    if (!productId || productId.trim().length === 0) {
      throw new Error('Product ID is required');
    }
    
    // Delete the product from the database
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);
    
    if (error) {
      logger.error('Product deletion failed', 'Products', { error, productId });
      throw error;
    }
    
    logger.info('Product deleted successfully', 'Products', { productId });
    
    // Clear cache
    APIOptimizer.clearCache('products');
    
    return { success: true };
  } catch (err) {
    logger.error('Product deletion error', 'Products', { error: err, productId });
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'An unknown error occurred' 
    };
  }
};